import { StateCreator } from 'zustand';

// Actions
import { getAllProjects } from '@/actions/client';

// Types
import { ProjectsSlice, ProjectsState } from './types';
import { Project } from '@shared/types';

// Utils
import { showErrorToast } from '@/utils/errorHandlers';

const initialProjectsState: ProjectsState = {
  projects: [],
  isProjectsLoading: false,
  isProjectsFetched: false,
};

export const createProjectsSlice: StateCreator<
  ProjectsSlice,
  [],
  [],
  ProjectsSlice
> = (set, get) => ({
  ...initialProjectsState,

  setProjects: (projects) => set({ projects }),
  setProjectsLoading: (isProjectsLoading) => set({ isProjectsLoading }),
  setProjectsFetched: (isProjectsFetched) => set({ isProjectsFetched }),

  // WebSocket update handlers
  setArchiveProject: (code: string, archive: boolean) => {
    const { projects } = get();
    const updatedProjects = projects.map((project) =>
      project.projectInfo.code === code
        ? {
            ...project,
            projectInfo: {
              ...project.projectInfo,
              archived: archive,
            },
          }
        : project,
    );
    set({ projects: updatedProjects });
  },

  removeProject: (projectIds: string[]) => {
    const { projects } = get();
    const filteredProjects = projects.filter(
      (project) => !projectIds.includes(project.projectInfo.id),
    );
    set({ projects: filteredProjects });
  },

  addProject: (newProject: Project) => {
    const { projects } = get();
    const projectExists = projects.some(
      (project) => project.projectInfo.id === newProject.projectInfo.id,
    );

    if (!projectExists) {
      set({ projects: [...projects, newProject] });
    }
  },

  fetchProjects: async ({ requestConfig, force = false } = {}) => {
    const { isProjectsLoading, isProjectsFetched, projects } = get();

    // Skip if already loading
    if (isProjectsLoading) return;

    // Determine if we need to fetch data
    const shouldFetch =
      // Not fetched before
      !isProjectsFetched ||
      // Has isFetched flag but no data
      (isProjectsFetched && projects.length === 0) ||
      // Force refresh
      force;

    if (!shouldFetch) return;

    try {
      set({ isProjectsLoading: true });

      const { data, error } = await getAllProjects({
        requestConfig,
      });

      if (error || !data) {
        set({
          projects: [],
          isProjectsLoading: false,
          isProjectsFetched: false,
        });
        showErrorToast(error);

        return;
      }

      const projectFormatted = data.map((project) => ({
        projectInfo: project,
      }));

      set({
        projects: projectFormatted || [],
        isProjectsLoading: false,
        isProjectsFetched: true,
      });
    } catch {
      set({
        projects: [],
        isProjectsLoading: false,
        isProjectsFetched: false,
      });
    }
  },

  resetProjects: () => set(initialProjectsState),
});
